/* global globalThis */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import ConfirmModal from "../components/ConfirmModal";
import Modal from "../components/Modal";
import StatusBadge from "../components/StatusBadge";
import {
	buildTalkPayload,
	emptyTalkForm,
	getTalkApiFieldErrors,
	mapTalkToForm,
	TalkFields,
	TALK_SAVE_FALLBACK_MESSAGE,
	validateTalkForm,
} from "../components/TalkFormFields";
import {
	deleteTalk,
	generateTalkCertificates,
	getTalk,
	importTalkParticipants,
	listTalkParticipants,
	updateTalk,
	downloadTalksTemplate,
} from "../api/talks";
import { listUsers } from "../api/users";
import { formatDate, formatDateTime } from "../../utils/date";
import { translateStatusLabel } from "../../utils/statusLabels";
import { capitalizeFirstLetter } from "../../utils/letters";

const TALK_STATUS_META = {
	open: { label: "Abierta", className: "#78bdc24f", textColor: "#4780a3" },
	finished: { label: "Finalizada", className: "#c499e756", textColor: "#a063d1" },
	cancelled: { label: "Cancelada", className: "#a3b1c056", textColor: "#555c63" },
};

const ATTENDANCE_STATUS_META = {
	confirmed: { label: "Confirmado", className: "#acc2784f", textColor: "var(--dark-green)" },
	pending: { label: "Pendiente", className: "#ddcb6273", textColor: "#776a21" },
	absent: { label: "Ausente", className: "#ee863257", textColor: "#97531b" },
};

function parseFilename(contentDisposition, fallback) {
	const match = /filename="?([^"]+)"?/i.exec(contentDisposition || "");
	return match?.[1] || fallback;
}

function renderNamedBadge(meta, fallback) {
	return (
		<span
			className={`status ${!meta ? "text-bg-secondary" : ""}`}
			style={{
				backgroundColor: meta?.className,
				color: meta?.textColor
			}}
		>
			{meta?.label || translateStatusLabel(fallback, fallback)}
		</span>
	);
}

function isValidImportFile(file) {
	if (!file?.name) return false;
	const fileName = file.name.toLowerCase();
	return fileName.endsWith(".xlsx") || fileName.endsWith(".xlsm");
}

function buildParticipantsTable(participants, loading) {
	if (loading) {
		return <span>Cargando participantes...</span>;
	}

	if (participants.length === 0) {
		return (
			<span className="text-muted fs-6 pt-2">No hay participantes cargados para esta actividad.</span>
		);
	}

	return (
		<div className="table-responsive table-wrapper-scroll">
			<table className="table table-sm table-hover mb-0 align-middle table-organizations-nowrap">
				<thead className="table-light">
					<tr>
						<th><span>RUT</span></th>
						<th><span>Nombre</span></th>
						<th><span>Correo electrónico</span></th>
						<th><span>Usuario vinculado</span></th>
						<th><span>Asistencia</span></th>
						<th><span>Certificado</span></th>
					</tr>
				</thead>
				<tbody>
					{participants.map((participant) => (
						<tr key={participant.id}>
							<td>
								<span className="fw-semibold">
									{participant.rut}
								</span>
							</td>
							<td>
								<span>{participant.full_name}</span>
							</td>
							<td>
								<span>{participant.email || "-"}</span>
							</td>
							<td>
								<span>
									{participant.linked_user_display ||
										"No vinculado"}
								</span>
							</td>
							<td>
								{renderNamedBadge(
									ATTENDANCE_STATUS_META[
									participant.attendance_status
									],
									participant.attendance_status || "-",
								)}
							</td>
							<td>
								<StatusBadge
									active={Boolean(
										participant.certificate_generated,
									)}
									title="Certificado generado"
								/>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export default function TalksDetail() {
	const navigate = useNavigate();
	const { id } = useParams();
	const talkId = Number(id);

	const [talk, setTalk] = useState(null);
	const [participants, setParticipants] = useState([]);
	const [loading, setLoading] = useState(true);
	const [participantsLoading, setParticipantsLoading] = useState(true);
	const [teachers, setTeachers] = useState([]);
	const [alert, setAlert] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [form, setForm] = useState(emptyTalkForm);
	const [fieldErrors, setFieldErrors] = useState({});
	const [saving, setSaving] = useState(false);
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const [uploadFile, setUploadFile] = useState(null);
	const [uploadError, setUploadError] = useState("");
	const [isDragActive, setIsDragActive] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [downloadingTemplate, setDownloadingTemplate] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const uploadInputRef = useRef(null);
	const dragDepthRef = useRef(0);

	function resetUploadState() {
		setUploadFile(null);
		setUploadError("");
		setIsDragActive(false);
		dragDepthRef.current = 0;
		if (uploadInputRef.current) {
			uploadInputRef.current.value = "";
		}
	}

	function handleSelectedUploadFile(file) {
		if (!file) {
			setUploadFile(null);
			setUploadError("");
			return;
		}

		if (!isValidImportFile(file)) {
			setUploadFile(null);
			setUploadError(
				"Selecciona un archivo Excel válido con extensión .xlsx o .xlsm.",
			);
			if (uploadInputRef.current) {
				uploadInputRef.current.value = "";
			}
			return;
		}

		setUploadFile(file);
		setUploadError("");
	}

	function handleUploadDragEnter(event) {
		event.preventDefault();
		event.stopPropagation();
		dragDepthRef.current += 1;
		setIsDragActive(true);
	}

	function handleUploadDragOver(event) {
		event.preventDefault();
		event.stopPropagation();
		if (!isDragActive) {
			setIsDragActive(true);
		}
	}

	function handleUploadDragLeave(event) {
		event.preventDefault();
		event.stopPropagation();
		dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
		if (dragDepthRef.current === 0) {
			setIsDragActive(false);
		}
	}

	function handleUploadDrop(event) {
		event.preventDefault();
		event.stopPropagation();
		dragDepthRef.current = 0;
		setIsDragActive(false);
		const file = event.dataTransfer?.files?.[0] || null;
		handleSelectedUploadFile(file);
	}

	async function loadTalk() {
		setLoading(true);
		try {
			const data = await getTalk(talkId);
			setTalk(data);
		} catch {
			setAlert({
				type: "danger",
				msg: "No se pudo cargar la certificacion seleccionada.",
			});
		} finally {
			setLoading(false);
		}
	}

	async function loadParticipants() {
		setParticipantsLoading(true);
		try {
			const data = await listTalkParticipants(talkId);
			setParticipants(Array.isArray(data) ? data : []);
		} catch {
			setParticipants([]);
			setAlert(
				(prev) =>
					prev ?? {
						type: "danger",
						msg: "No se pudieron cargar los participantes de la certificacion.",
					},
			);
		} finally {
			setParticipantsLoading(false);
		}
	}

	useEffect(() => {
		loadTalk();
		loadParticipants();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [talkId]);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const data = await listUsers({});
				const list = Array.isArray(data) ? data : (data.results ?? []);
				if (!cancelled) {
					setTeachers(
						list.filter(
							(user) =>
								user.role === "teacher" ||
								user.role === "admin",
						),
					);
				}
			} catch {
				if (!cancelled) setTeachers([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	function openEdit() {
		if (!talk) return;
		setForm(mapTalkToForm(talk));
		setFieldErrors({});
		setModalOpen(true);
	}

	function closeEdit() {
		if (saving) return;
		setModalOpen(false);
		setFieldErrors({});
	}

	async function submitEdit(event) {
		event.preventDefault();
		const nextErrors = validateTalkForm(form);
		setFieldErrors(nextErrors);
		if (Object.keys(nextErrors).length > 0) return;

		setSaving(true);
		setAlert(null);
		try {
			await updateTalk(talkId, buildTalkPayload(form));
			setModalOpen(false);
			setFieldErrors({});
			setAlert({
				type: "success",
				msg: "Certificacion actualizada correctamente.",
			});
			await loadTalk();
		} catch (error) {
			const apiData = error.response?.data;
			setFieldErrors(getTalkApiFieldErrors(apiData));
			setAlert({
				type: "danger",
				msg:
					typeof apiData?.detail === "string"
						? apiData.detail
						: TALK_SAVE_FALLBACK_MESSAGE,
			});
		} finally {
			setSaving(false);
		}
	}

	async function submitImport() {
		if (!uploadFile) return;
		setUploading(true);
		setUploadError("");
		try {
			const result = await importTalkParticipants(talkId, uploadFile);
			setAlert({
				type: "success",
				msg: `Importación completada. Nuevos: ${result.created_count}, actualizados: ${result.updated_count}, inválidos: ${result.invalid_rows.length}.`,
			});
			setUploadModalOpen(false);
			resetUploadState();
			await Promise.all([loadTalk(), loadParticipants()]);
		} catch (error) {
			setUploadError(
				error.response?.data?.detail ||
				"No se pudo importar el archivo Excel.",
			);
			setAlert({
				type: "danger",
				msg:
					error.response?.data?.detail ||
					"No se pudo importar el archivo Excel.",
			});
		} finally {
			setUploading(false);
		}
	}

	async function handleGenerateCertificates() {
		if (!talk) return;
		setDownloading(true);
		try {
			const { blob, contentDisposition } =
				await generateTalkCertificates(talkId);
			const filename = parseFilename(
				contentDisposition,
				`certificados_actividad_${talk.code || talk.id}.zip`,
			);
			const url = globalThis.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			link.remove();
			globalThis.URL.revokeObjectURL(url);
			setAlert({
				type: "success",
				msg: "ZIP de certificados generado correctamente.",
			});
			await Promise.all([loadTalk(), loadParticipants()]);
		} catch (error) {
			setAlert({
				type: "danger",
				msg:
					error.response?.data?.detail ||
					"No se pudieron generar los certificados.",
			});
		} finally {
			setDownloading(false);
		}
	}

	async function handleDownloadTemplate() {
		setDownloadingTemplate(true);
		try {
			const { blob, contentDisposition } = await downloadTalksTemplate();
			const filename = parseFilename(contentDisposition, "Plantilla_Participantes.xlsx");
			const url = globalThis.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			link.remove();
			globalThis.URL.revokeObjectURL(url);
		} catch (error) {
			setAlert({ type: "danger", msg: "No se pudo descargar la plantilla." });
		} finally {
			setDownloadingTemplate(false);
		}
	}

	async function confirmDelete() {
		setDeleteLoading(true);
		try {
			await deleteTalk(talkId);
			navigate("/backoffice/talks", {
				replace: true,
				state: {
					alert: {
						type: "success",
						msg: "Certificacion eliminada correctamente.",
					},
				},
			});
		} catch (error) {
			setAlert({
				type: "danger",
				msg:
					error.response?.data?.detail ||
					"No se pudo eliminar la certificacion.",
			});
		} finally {
			setDeleteLoading(false);
			setDeleteOpen(false);
		}
	}

	const participantsTable = buildParticipantsTable(
		participants,
		participantsLoading,
	);
	const talkStatusBadge = renderNamedBadge(
		TALK_STATUS_META[talk?.status],
		talk?.status || "-",
	);
	const speakerName =
		talk?.speaker_display || talk?.speaker_name || "Por definir";
	const latestImportLabel = talk?.latest_import?.created_at
		? formatDateTime(talk.latest_import.created_at)
		: "Sin importaciones";
	const invalidAndSkippedCount =
		(talk?.latest_import?.invalid_count ?? 0) +
		(talk?.latest_import?.skipped_count ?? 0);

	let detailContent = (
		<div className="card card-table mt-4 shadow-sm">
			<div className="card-body text-muted p-4">
				No fue posible cargar la certificacion.
			</div>
		</div>
	);

	if (loading) {
		detailContent = (
			<div className="card card-table mt-4 shadow-sm">
				<div className="card-body text-muted p-4">
					Cargando certificacion...
				</div>
			</div>
		);
	} else if (talk) {
		detailContent = (
			<>
				{/* BLOQUE 1: RESUMEN HORIZONTAL SUPERIOR */}
				<div className="row g-3 mt-1 mb-4 student-profile-kpis">
					<div className="col-12 col-sm-6 col-lg-3">
						<div className="card card-table shadow-sm lote h-100">
							<div className="card-body">
								<div>
									<span className="fw-semibold">Participantes</span>
								</div>
								<div className="fs-4 fw-semibold text-break">
									<span>{talk.participant_count ?? 0}</span>
								</div>
							</div>
						</div>
					</div>
					<div className="col-12 col-sm-6 col-lg-3">
						<div className="card card-table shadow-sm certi h-100">
							<div className="card-body">
								<div>
									<span className="fw-semibold">Certificados</span>
								</div>
								<div className="fs-4 fw-semibold text-break">
									<span>{talk.certificate_count ?? 0}</span>
								</div>
							</div>
						</div>
					</div>
					<div className="col-12 col-sm-6 col-lg-3">
						<div className="card card-table shadow-sm matri h-100">
							<div className="card-body">
								<div>
									<span className="fw-semibold">Relator</span>
								</div>
								<div className="fs-5 fw-semibold text-break">
									<span>{capitalizeFirstLetter(speakerName) || "-"}</span>
								</div>
							</div>
						</div>
					</div>
					<div className="col-12 col-sm-6 col-lg-3">
						<div className="card card-table shadow-sm import h-100">
							<div className="card-body">
								<div>
									<span className="fw-semibold">Última importación</span>
								</div>
								<div className="fs-5 fw-semibold text-break">
									<span>{latestImportLabel}</span>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* BLOQUE 2: TARJETAS DE IMPORTACIÓN Y CERTIFICADOS */}
				<div className="row g-3 mb-4 student-profile-kpis">
					<div className="col-12 col-xl-6">
						<section className="card card-table shadow-sm import h-100">
							<div className="card-body p-4 d-flex flex-column">
								<div className="d-flex align-items-center justify-content-start border-bottom flex-wrap gap-2 mb-4 pb-2">
									<h5 className="mb-0 fw-bold">Importar participantes</h5>
									<span className="text-muted fs-6"> - gestión de carga</span>
								</div>

								<div className="container-fluid p-0 flex-grow-1 d-flex flex-column justify-content-between">
									<div>
										<p className="mb-2">
											Carga Excel para crear o actualizar participantes vinculados a esta actividad.
										</p>
										<div className="pb-3 text-break">
											<span className="fw-semibold pe-2">Último lote:</span>
											<span>{talk.latest_import?.file_name || "Sin importaciones"}</span>
										</div>
									</div>
									{/* Contenedor responsivo para el botón para que no se aplaste */}
									<div className="row g-0">
										<div className="col-12 col-sm-auto">
											<button type="button" className="btn w-100 adm-save-btn-excel" onClick={() => setUploadModalOpen(true)} disabled={!talk || loading || uploading}>
												<i className="ri-file-upload-fill me-1" aria-hidden="true" />
												<span>Subir Excel</span>
											</button>
										</div>
									</div>
								</div>
							</div>
						</section>
					</div>
					<div className="col-12 col-xl-6">
						<section className="card card-table shadow-sm certi h-100">
							<div className="card-body p-4 d-flex flex-column">
								<div className="d-flex align-items-center justify-content-start border-bottom flex-wrap gap-2 mb-4 pb-2">
									<h5 className="mb-0 fw-bold">Certificado</h5>
									<span className="text-muted fs-6"> - emisión</span>
								</div>

								<div className="container-fluid p-0 flex-grow-1 d-flex flex-column justify-content-between">
									<div>
										<p className="mb-2">
											Genera el ZIP consolidado con los certificados disponibles para esta actividad.
										</p>
										<div className="pb-3 d-flex flex-wrap gap-3">
											<span className="fw-semibold">Participantes: {talk.participant_count ?? 0}</span>
											<span className="fw-semibold">Emitidos: {talk.certificate_count ?? 0}</span>
										</div>
									</div>
									{/* Contenedor responsivo para el botón */}
									<div className="row g-0">
										<div className="col-12 col-sm-auto">
											<button
												type="button"
												className="btn w-100 adm-save-btn"
												onClick={handleGenerateCertificates}
												disabled={!talk || loading || downloading}
											>
												<i className="ri-award-fill me-1" aria-hidden="true" />
												<span>{downloading ? "Generando..." : "Generar ZIP"}</span>
											</button>
										</div>
									</div>
								</div>
							</div>
						</section>
					</div>
				</div>

				{/* BLOQUE 3: INFORMACIÓN GENERAL */}
				<section className="card card-table shadow-sm mb-4">
					<div className="card-body p-4">
						<div className="d-flex justify-content-between  align-items-start flex-column flex-sm-row gap-3 mb-4 pb-2 border-bottom">
							<div>
								<h5 className="mb-1 fw-bold text-wrap text-break">Información general</h5>
								<p className="text-muted small mb-0">Datos principales de la actividad certificable, relator y emisión.</p>
							</div>
							<div className="d-flex flex-wrap align-items-center justify-content-sm-end gap-3">
								<span className="font-n status pe-sm-0">{talkStatusBadge}</span>
								<button type="button" className="btn-sm adm-upd-btn text-nowrap" onClick={openEdit} disabled={!talk || loading}>
									<i className="ri-edit-fill me-1 d-md-none d-inline" aria-hidden="true" />
									<span className="d-none d-md-inline">Editar</span>
								</button>
							</div>
						</div>

						<div className="container-fluid p-0">
							<div className="row g-3">
								<div className="col-12 col-sm-6 col-md-4 pb-2 text-break">
									<label className="mb-1 me-2 fw-semibold"><span>Código:</span></label>
									<span>{talk.code || "-"}</span>
								</div>
								<div className="col-12 col-sm-6 col-md-4 pb-2 text-break">
									<label className="mb-1 me-2 fw-semibold"><span>Horas:</span></label>
									<span>{talk.duration_hours ?? "-"}</span>
								</div>
								<div className="col-12 col-sm-6 col-md-4 pb-2 text-break">
									<label className="mb-1 me-2 fw-semibold"><span>Lugar:</span></label>
									<span>{capitalizeFirstLetter(talk.location) || "-"}</span>
								</div>
								<div className="col-12 col-sm-6 col-md-4 pb-2 text-break">
									<label className="mb-1 me-2 fw-semibold"><span>Inicio:</span></label>
									<span>{formatDate(talk.start_date) || "-"}</span>
								</div>
								<div className="col-12 col-sm-6 col-md-4 pb-2 text-break">
									<label className="mb-1 me-2 fw-semibold"><span>Término:</span></label>
									<span>{formatDate(talk.end_date) || "-"}</span>
								</div>
								<div className="col-12 col-sm-6 col-md-4 pb-2 text-break">
									<label className="mb-1 me-2 fw-semibold"><span>Emisión:</span></label>
									<span>{formatDate(talk.issue_date) || "-"}</span>
								</div>

								<h5 className="mb-0 fw-bold border-bottom pb-2 mt-3 w-100 text-wrap text-break">Autoridades & Relatoría</h5>

								<div className="col-12 col-md-6 pb-2 text-break">
									<label className="mb-1 me-2 fw-semibold"><span>Relator: </span></label>
									<span>{capitalizeFirstLetter(speakerName)}</span>
								</div>
								<div className="col-12 col-md-6 pb-2 text-break">
									<label className="mb-1 me-2 fw-semibold"><span>Cargo relator: </span></label>
									<span>{capitalizeFirstLetter(talk.speaker_role) || "-"}</span>
								</div>
								<div className="col-12 col-md-6 pb-2 text-break">
									<label className="mb-1 me-2 fw-semibold"><span>Representante legal: </span></label>
									<span>{capitalizeFirstLetter(talk.legal_representative_name) || "-"}</span>
								</div>
								<div className="col-12 col-md-6 pb-2 text-break">
									<label className="mb-1 me-2 fw-semibold"><span>Cargo rep. legal: </span></label>
									<span>{capitalizeFirstLetter(talk.legal_representative_role) || "-"}</span>
								</div>

								<h5 className="mb-0 fw-bold border-bottom pb-2 mt-3 w-100 text-wrap text-break">Detalles adicionales</h5>

								<div className="col-12 pb-2 text-break">
									<label className="mb-1 me-2 fw-semibold"><span>Descripción: </span></label>
									<span>{capitalizeFirstLetter(talk.description) || "-"}</span>
								</div>
								<div className="col-12 pb-2 text-break">
									<label className="mb-1 me-2 fw-semibold"><span>Notas: </span></label>
									<span>{talk.notes || "-"}</span>
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* BLOQUE 4: PANEL "RESUMEN" Y PANEL "ÚLTIMO LOTE" */}
				<div className="row g-3 mb-4 student-profile-kpis">
					<div className="col-12 col-xl-6">
						<div className="card card-table shadow-sm h-100 import">
							<div className="card-body p-4">
								<div className="pb-2 border-bottom fw-semibold mb-3">
									<h5 className="mb-0 fw-bold text-wrap text-break">Importación del último lote</h5>
								</div>
								<div className="d-flex justify-content-between border-bottom py-2 flex-wrap">
									<span className="fw-semibold pe-2">Archivo</span>
									<strong className="text-break text-end">{talk.latest_import?.file_name || "-"}</strong>
								</div>
								<div className="d-flex justify-content-between border-bottom py-2 flex-wrap">
									<span className="fw-semibold pe-2">Nuevos</span>
									<strong className="text-break text-end">{talk.latest_import?.created_count ?? 0}</strong>
								</div>
								<div className="d-flex justify-content-between border-bottom py-2 flex-wrap">
									<span className="fw-semibold pe-2">Actualizados</span>
									<strong className="text-break text-end">{talk.latest_import?.updated_count ?? 0}</strong>
								</div>
								<div className="d-flex justify-content-between py-2 flex-wrap">
									<span className="fw-semibold pe-2">Inválidos/Omitidos</span>
									<strong className="text-break text-end">{invalidAndSkippedCount}</strong>
								</div>
							</div>
						</div>
					</div>
					<div className="col-12 col-xl-6">
						<div className="card card-table shadow-sm h-100 lote">
							<div className="card-body p-4 d-flex flex-column">
								<div className="pb-2 border-bottom fw-semibold mb-3">
									<h5 className="mb-0 fw-bold">Participantes</h5>
								</div>
								<div className="container-fluid p-0 flex-grow-1 d-flex flex-column justify-content-between">
									<div>
										<p className="mb-3">
											Listado actualizado de asistentes, su estado de asistencia y emisión de certificado.
										</p>
										<div className="pb-3 w-100 overflow-hidden">
											{participantsTable}
										</div>
									</div>
									<div className="row g-0">
										<div className="col-12 col-sm-auto">
											<button type="button" className="btn admin-btn w-100 text-nowrap" onClick={() => setUploadModalOpen(true)} disabled={!talk || loading || uploading}>
												<i className="ri-upload-2-line me-1" aria-hidden="true" />
												<span>Importar</span>
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</>
		);
	}

	return (
		<div className="container-fluid student-profile-page">
			<div className="btn-back-container">
				<button onClick={() => navigate("/backoffice/talks")}>
					<i className="ri-arrow-go-back-line d-inline d-md-none"></i>
					<span className="d-none d-md-inline">Volver</span>
				</button>
			</div>

			{/* Ajuste Responsivo Principal: flex-column en celulares para no aplastar el título, flex-xl-row en pantallas grandes */}
			<div className="container-principal d-flex justify-content-between align-items-start align-items-xl-center flex-column flex-xl-row gap-3">

				{/* Contenedor del título con restricción minWidth 0 para obligarlo a saltar de línea */}
				<div className="student-profile-page__title-block w-100" style={{ minWidth: 0 }}>
					<h2 className="m-0 text-wrap text-break" style={{ wordWrap: "break-word" }}>
						{capitalizeFirstLetter(talk?.name) || "Detalle de certificación"}
					</h2>
					<div className="admin-subtittle mt-1 text-wrap" style={{ fontSize: 14 }}>
						<span>Revisa participantes, importaciones y certificados de esta actividad.</span>
					</div>
				</div>

				{/* Botones de acción envueltos con flex-fill para celular y ancho auto para escritorio */}
				<div className=" d-flex gap-2 flex-wrap w-100 w-xl-auto mt-2 mt-xl-0 justify-content-start justify-content-xl-end">
					<button type="button" className="btn adm-upd-btn flex-fill flex-sm-grow-0 text-nowrap" onClick={openEdit} disabled={!talk || loading}>
						<i className="ri-edit-fill me-1 d-md-none d-inline" aria-hidden="true" />
						<span className="d-none d-md-inline">Editar</span>
					</button>
					<button type="button" className="btn adm-save-btn-excel flex-fill flex-sm-grow-0 text-nowrap" onClick={() => setUploadModalOpen(true)} disabled={!talk || loading || uploading}>
						<i className="ri-file-upload-fill me-1" aria-hidden="true" />
						<span>Subir Excel</span>
					</button>
					<button type="button" className="btn adm-save-btn flex-fill flex-sm-grow-0 text-nowrap" onClick={handleGenerateCertificates} disabled={!talk || loading || downloading}>
						<i className="ri-award-fill me-1" aria-hidden="true" />
						<span>{downloading ? "Generando certificados..." : "Generar certificados"}</span>
					</button>
					<button type="button" className="btn adm-del-btn flex-fill flex-sm-grow-0 text-nowrap" onClick={() => setDeleteOpen(true)} disabled={!talk || loading || deleteLoading}>
						<i className="ri-delete-bin-line me-1 d-md-none d-inline" aria-hidden="true" />
						<span className="d-none d-md-inline">Eliminar</span>
					</button>
				</div>
			</div>

			{alert && (
				<div className={`alert alert-${alert.type} mt-3 mb-3`} role="alert">
					{alert.msg}
				</div>
			)}

			{detailContent}

			{/* MODALES INTACTOS */}
			<Modal title="Editar certificacion" open={modalOpen} onClose={closeEdit} footer={
				<>
					<button type="button" className="btn adm-del-btn" onClick={closeEdit} disabled={saving}><span>Cancelar</span></button>
					<button type="submit" form="talk-detail-form" className="btn adm-save-btn" disabled={saving}><span>{saving ? "Guardando..." : "Guardar cambios"}</span></button>
				</>
			}>
				<form id="talk-detail-form" onSubmit={submitEdit}>
					<TalkFields fieldErrors={fieldErrors} form={form} setForm={setForm} teachers={teachers} />
				</form>
			</Modal>

			<Modal title="Importar asistentes" open={uploadModalOpen} onClose={() => { if (uploading) return; setUploadModalOpen(false); resetUploadState(); }} footer={
				<>
					<button type="button" className="btn adm-del-btn" onClick={() => { setUploadModalOpen(false); resetUploadState(); }} disabled={uploading}><span>Cancelar</span></button>
					<button type="button" className="btn adm-save-btn" onClick={submitImport} disabled={uploading || !uploadFile}><span>{uploading ? "Importando..." : "Importar Excel"}</span></button>
				</>
			}>
				<div className="mb-3 text-muted small">
					El archivo debe contener las columnas <strong>RUT</strong> y <strong>Nombre Completo</strong>. La columna correo es opcional.
					<div className="mt-2">
						<button type="button" className="btn btn-outline-primary btn-sm" onClick={handleDownloadTemplate} disabled={downloadingTemplate}>
							<i className="ri-download-2-line me-1"></i> {downloadingTemplate ? "Descargando..." : "Descargar plantilla obligatoria"}
						</button>
					</div>
				</div>
				<input ref={uploadInputRef} id="talk-participants-upload" type="file" className="d-none" accept=".xlsx,.xlsm" onChange={(event) => handleSelectedUploadFile(event.target.files?.[0] || null)} />
				<label htmlFor="talk-participants-upload" className={`talk-upload-dropzone${isDragActive ? " is-drag-active" : ""}${uploadFile ? " has-file" : ""}`} onDragEnter={handleUploadDragEnter} onDragOver={handleUploadDragOver} onDragLeave={handleUploadDragLeave} onDrop={handleUploadDrop}>
					<div className="talk-upload-dropzone__title">Arrastra tu archivo aquí</div>
					<div className="talk-upload-dropzone__hint">o usa el botón para seleccionar un Excel desde tu equipo.</div>
					<span className="btn talk-upload-dropzone__button">Seleccionar archivo</span>
					<div className="talk-upload-dropzone__meta">Formatos permitidos: .xlsx y .xlsm</div>
					{uploadFile && (
						<div className="talk-upload-dropzone__selected"><strong>Archivo seleccionado:</strong> {uploadFile.name}</div>
					)}
				</label>
				{uploadError && <div className="alert alert-danger py-2 mt-3 mb-0" role="alert">{uploadError}</div>}
			</Modal>

			<ConfirmModal open={deleteOpen} onClose={() => setDeleteOpen(false)} onConfirm={confirmDelete} title="Eliminar certificacion" message={`Se eliminará la certificacion ${talk?.name || "seleccionada"} y su historial asociado.`} confirmLabel={deleteLoading ? "Eliminando..." : "Eliminar"} loading={deleteLoading} />
		</div>
	);
}